/**
 * BrightDocs Application Entry Point
 *
 * Initialises the React application with all required providers:
 * - MUI ThemeProvider for consistent styling
 * - TanStack React Query for server-state management
 * - Notistack for toast notifications
 * - Google OAuth for authentication
 * - React Router for client-side routing
 * - Custom AuthProvider for session management
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { BrowserRouter } from 'react-router-dom';

import theme from './theme';
import { AuthProvider } from './auth/AuthProvider';
import App from './App';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '';

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <SnackbarProvider
          maxSnack={3}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <GoogleOAuthProvider clientId={googleClientId}>
            <BrowserRouter>
              <a id="skip-to-content" href="#main-content">
                Skip to main content
              </a>
              <AuthProvider>
                <App />
              </AuthProvider>
            </BrowserRouter>
          </GoogleOAuthProvider>
        </SnackbarProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </React.StrictMode>,
);
